import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import { useResource } from "hooks/useResource";

import { Icon, IconType } from "components/_icons/Icon";
import { Button } from "components/_buttons/Button";
import { TextTruncator } from "components/TextTruncator";
import { Text } from "components/Text";

import { ButtonSize, ButtonVariant } from "ts/enums/button";
import { Color } from "ts/enums/color";
import { PillType } from "ts/enums/pillType";
import { ToggeablePill } from "components/_pills/ToggeablePill";

type Props = {
  pillList: string[];
  inputHeaderKey: string;
  placeholderKey: string;
  height: string;
  isSelectAllPill?: boolean;
  inputMaxLength?: number;
  hasErrors?: boolean;
  showLoadMore?: boolean;
  canEdit?: boolean;
  allowMultipleLinesInput?: boolean;
  loadMoreHandler?: () => void;
  submitListItem: (list: string) => void;
  removeListItem: (item: Record<any, any>) => void;
  clearAll: () => void;
};

export const PillInput = ({
  pillList,
  height,
  inputHeaderKey,
  placeholderKey,
  isSelectAllPill,
  inputMaxLength,
  hasErrors,
  showLoadMore,
  canEdit,
  allowMultipleLinesInput,
  loadMoreHandler,
  submitListItem,
  clearAll,
  removeListItem,
}: Props) => {
  const [isInputActive, setIsInputActive] = useState<boolean>(false);
  const [userInput, setUserInput] = useState<string>("");

  const { getResource } = useResource();

  const inputRef = useRef(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && userInput.trim() !== "") {
      e.preventDefault();
      addCurrentUserInput();
    }
  };

  const handleOnBlur = () => {
    setIsInputActive(false);
    addCurrentUserInput();
  };

  const addCurrentUserInput = () => {
    // Split the input so each line is a new item
    const inputSplit = userInput.split(/[\r\n]+/);
    inputSplit.forEach((input) => {
      const trimmedInput = input.trim();
      if (trimmedInput !== "") {
        submitListItem(trimmedInput);
      }
    });
    setUserInput("");
  };

  const onInputMouseDown = () => {
    setIsInputActive(true);
    inputRef.current.focus();
  };

  useEffect(() => {
    if (hasErrors) inputRef.current.focus();
  }, [hasErrors]);

  return (
    <StyledParentContainer>
      <StyledHeaderContainer>
        <Text resource={inputHeaderKey} />
        {pillList.length > 0 && canEdit && (
          <Button
            size={ButtonSize.sm}
            variant={ButtonVariant.secondary}
            style={{
              gap: "6px",
              padding: "6px 12px",
            }}
            onClick={clearAll}
          >
            <Icon type={IconType.x} size={16} />
            <Text resource="button.clearAll" />
          </Button>
        )}
      </StyledHeaderContainer>

      <StyledUserInputFieldContainer
        isInputActive={isInputActive}
        onClick={onInputMouseDown}
        height={height}
        hasErrors={hasErrors}
        canEdit={canEdit}
      >
        <StyledInputPillContainer>
          {pillList.length > 0 &&
            pillList.map((pillItem, index) => (
              <ToggeablePill
                key={index}
                pillType={
                  isSelectAllPill && index === 0 ? PillType.SelectAll : PillType.SingleSelect
                }
                customStyles={{ maxWidth: "325px", maxHeight: "42px", marginTop: 0 }}
                handleDelete={canEdit ? () => removeListItem({ pillItem, index }) : null}
              >
                <TextTruncator id={index} value={pillItem} />
              </ToggeablePill>
            ))}
          {showLoadMore && (
            <StyledLoadMoreButton onClick={loadMoreHandler}>
              <Text resource="button.loadMore" />
            </StyledLoadMoreButton>
          )}
          {canEdit &&
            (allowMultipleLinesInput ? (
              <StyledTextArea
                onFocus={() => setIsInputActive(true)}
                onBlur={handleOnBlur}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={getResource(placeholderKey)}
                value={userInput}
                ref={inputRef}
                maxLength={inputMaxLength}
              />
            ) : (
              <StyledInputField
                onFocus={() => setIsInputActive(true)}
                onBlur={handleOnBlur}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={getResource(placeholderKey)}
                value={userInput}
                ref={inputRef}
                maxLength={inputMaxLength}
              />
            ))}
        </StyledInputPillContainer>
      </StyledUserInputFieldContainer>
    </StyledParentContainer>
  );
};

const StyledParentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StyledHeaderContainer = styled.div`
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 15px;
`;

const StyledUserInputFieldContainer = styled.div<{
  isInputActive: boolean;
  height: string;
  hasErrors: boolean;
  canEdit: boolean;
}>`
  border: ${({ isInputActive, hasErrors, canEdit }) =>
    `2px solid ${
      hasErrors ? Color.red50 : isInputActive && canEdit ? Color.blue50 : Color.blue20
    }`};
  border-radius: 5px;
  padding: 11px;
  overflow-y: auto;
  cursor: ${({ canEdit }) => (canEdit ? "text" : "default")};
  ${({ height }) =>
    height &&
    css`
      height: ${height};
    `};
`;

const StyledInputPillContainer = styled.div`
  display: flex;
  align-items: start;
  align-content: flex-start;
  flex-wrap: wrap;
`;

const StyledTextArea = styled.textarea`
  border: none;
  outline: none;
  margin: 3px;
  padding: 8px 5px;
  flex-grow: 1;
  resize: none;
  font-size: 15px;
  color: ${Color.gray50};
`;

const StyledInputField = styled.input`
  border: none;
  outline: none;
  margin: 3px;
  padding: 8px 5px;
  flex-grow: 1;
  color: ${Color.gray50};
`;

const StyledLoadMoreButton = styled.button`
  height: 29px;
  width: 100px;
  padding: 6px 12px;
  border: none;
  background-color: ${Color.sky15};
  color: ${Color.blue50};
  font-weight: bold;
`;
